import { Download, Eye, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppButton from "../../../components/ui/AppButton";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { MySelect } from "../../../components/ui/Select";
import ThreeDotsMenu from "../../../components/ui/ThreeDotsMenu";
import { usePermissions } from "../../../hooks/usePermissions";
import { cn } from "../../../lib/utils";
import { Role } from "../@types";
import AddRoleModal from "../AddRole";
import EditRoleModal from "../EditRole";
import { UserList } from "../ui/UserList";
import { useGetAllRolesQuery } from "../userApiSlice";

const RoleDashboardTab = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith("/super-admin")
    ? "/super-admin"
    : "/admin";
  const { canCreate, canUpdate, canDelete, canExport } = usePermissions();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateAddedFilter, setDateAddedFilter] = useState("");
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);

  const { data, isLoading, error: loadError, refetch } = useGetAllRolesQuery();

  const roles = data?.data?.data ?? [];

  const handleExportCSV = () => {
    console.log("Export CSV functionality");
  };

  const handleAddRole = (open: boolean) => {
    setIsAddRoleModalOpen(open);
  };

  // Table columns configuration
  const columns: Column<Role>[] = [
    {
      key: "name",
      header: "Role",
      width: "150px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "assignees",
      header: "User name",
      width: "200px",
      render: (value: string, item: Role) => (
        <UserList users={item.assignees ? item.assignees : []} />
      ),
    },

    {
      key: "actions",
      header: "",
      width: "12",
      align: "center" as const,
      render: (value: any, item: Role) => (
        <ThreeDotsMenu
          actions={[
            {
              label: "View role",
              onClick: () => navigate(`${basePath}/role/${item.id}`),
              icon: <Eye className="w-4 h-4" />,
            },
            ...(canUpdate("Roles")
              ? [
                  {
                    label: "Edit role",
                    onClick: () => setEditingRoleId(item.id),
                    icon: <Pencil className="w-4 h-4" />,
                  },
                ]
              : []),
            ...(canDelete("Roles")
              ? [
                  {
                    label: "Delete role",
                    onClick: () => {},
                    icon: <Trash2 className="w-4 h-4" />,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <div className="space-y-6">
        {/* Filters and Actions */}
        <div className="bg-white rounded-[10px] p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="text-[16px] pl-9 pr-4 py-2 border border-[#F6F6F6] bg-[#F6F6F6] rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary w-full sm:w-[367px] h-[42px]"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative w-full sm:w-auto">
                <MySelect<string>
                  name={"name"}
                  id={"id"}
                  label={""}
                  options={[
                    "Today",
                    "This week",
                    "This month",
                    "This year",
                    "All time",
                  ]}
                  getValue={(option: string) => option}
                  getName={(option: string) => option}
                  value={dateAddedFilter}
                  onChange={(e) => setDateAddedFilter(e)}
                  onValChange={(e) => setDateAddedFilter(e)}
                  placeholder={"Date"}
                  disabled={isLoading}
                  className={cn(
                    "focus:ring-primary focus:border-primary",
                    {
                      "opacity-50": isLoading,
                    },
                    "w-full sm:w-[121px] h-[42px] text-[16px] text-[#535353] appearance-none bg-white border border-[#CECECE] rounded-lg px-4 py-2 pr-8 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
                  )}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 [&>*]:flex-1 sm:[&>*]:flex-none">
            {canExport("Roles") && (
              <AppButton
                appearance="soft"
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 border border-[#CECECE] bg-white rounded-lg text-hColor hover:bg-gray-50 transition-colors sm:min-w-[146px] justify-center h-[42px]"
              >
                <Download className="h-4 w-4 mr-2" />
                Export csv
              </AppButton>
            )}
            {canCreate("Roles") && (
              <AppButton
                onClick={() => handleAddRole(true)}
                className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/80 transition-colors sm:min-w-[146px] justify-center h-[42px]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add role
              </AppButton>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden space-y-6">
          <Table
            data={roles || []}
            columns={columns}
            loading={isLoading}
            error={loadError}
            emptyMessage="No roles found"
          />
        </div>
      </div>

      {/* Add Role Modal */}
      {isAddRoleModalOpen && (
        <AddRoleModal
          isOpen={isAddRoleModalOpen}
          onClose={() => {
            refetch();
            handleAddRole(false);
          }}
        />
      )}

      {/* Edit Role Modal */}
      {editingRoleId && (
        <EditRoleModal
          roleId={editingRoleId}
          onClose={() => {
            refetch();
            setEditingRoleId(null);
          }}
        />
      )}
    </div>
  );
};

export default RoleDashboardTab;
